import React, { useEffect, useState } from "react";
import axios from "axios";
import { useSearchParams, Link } from "react-router-dom";
import { API } from "../context/AuthContext";
import { Loader2, CheckCircle2, AlertCircle, Mail } from "lucide-react";

export default function VerifyEmail() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [state, setState] = useState("loading"); // loading | success | error
  const [errorMsg, setErrorMsg] = useState("");
  const [verifiedEmail, setVerifiedEmail] = useState("");

  useEffect(() => {
    (async () => {
      if (!token) {
        setState("error");
        setErrorMsg("This verification link is missing a token. Open the link from your email.");
        return;
      }
      try {
        const res = await axios.post(`${API}/auth/verify-email`, { token });
        setVerifiedEmail(res.data?.email || "");
        setState("success");
      } catch (err) {
        setState("error");
        setErrorMsg(err?.response?.data?.detail || "Could not verify this link");
      }
    })();
  }, [token]);

  return (
    <div className="max-w-md mx-auto px-6 py-20" data-testid="verify-email-page">
      <span className="text-xs tracking-[0.2em] uppercase text-[#C66A51]">Account</span>
      <h1 className="font-display text-3xl text-[#2C4C3B] mt-1">Email verification</h1>

      {state === "loading" && (
        <div className="mt-8 p-6 rounded-2xl bg-white border border-[#E2E8F0] flex items-center gap-3" data-testid="verify-email-loading">
          <Loader2 className="w-5 h-5 animate-spin text-[#2C4C3B]" />
          <p className="text-sm text-[#52606D]">Confirming your email…</p>
        </div>
      )}

      {state === "success" && (
        <div className="mt-8 p-6 rounded-2xl bg-[#E6F0E9] border border-[#2C4C3B]/15" data-testid="verify-email-success">
          <CheckCircle2 className="w-6 h-6 text-[#2C4C3B]" />
          <p className="text-sm text-[#2C4C3B] font-semibold mt-3">Email verified.</p>
          {verifiedEmail && <p className="text-xs text-[#52606D] mt-1">{verifiedEmail} is now confirmed.</p>}
          <Link to="/search" className="inline-block mt-4 px-5 py-2.5 rounded-full bg-[#2C4C3B] hover:bg-[#1F3A2C] text-white text-sm font-medium" data-testid="verify-email-cta">
            Build a list →
          </Link>
        </div>
      )}

      {state === "error" && (
        <div className="mt-8 p-6 rounded-2xl bg-[#FDF6F0] border border-[#C66A51]/30" data-testid="verify-email-error">
          <AlertCircle className="w-6 h-6 text-[#C66A51]" />
          <p className="text-sm text-[#9C422A] font-semibold mt-3">{errorMsg}</p>
          <p className="text-xs text-[#52606D] mt-2 flex items-center gap-1.5">
            <Mail className="w-3.5 h-3.5" /> Sign in and use "Resend verification" from your account menu.
          </p>
        </div>
      )}
    </div>
  );
}
